<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { mdiContentSave } from '@mdi/js'
import { cloneDeep, isEqual } from 'lodash'
import { getRoleName } from '@/shared/utils/user'
import { useUserStore } from '@/modules/user/stores/user.store'
import { useToaster } from '@/modules/toaster/composables/use-toaster'
import { DEFAULT_PROFILE } from '@/constants'
import type { Profile } from '@/modules/user/models/user.model'
import type { Validate } from '@/shared/types/validation.types'
import BaseModal from '@/shared/components/modal/BaseModal.vue'
import BodyModal from '@/shared/components/modal/BodyModal.vue'
import FooterModal from '@/shared/components/modal/FooterModal.vue'
import TextInput from '@/shared/components/input/TextInput.vue'
import PrimaryButton from '@/shared/components/button/PrimaryButton.vue'
import DefaultButton from '@/shared/components/button/DefaultButton.vue'
import HeaderModal from '@/shared/components/modal/HeaderModal.vue'

const open = defineModel<boolean>('open', { required: true })
const userStore = useUserStore()
const isSaving = ref<boolean>(false)
const validate = ref<{ [key: string]: Validate }>({})
const original = ref<Profile>({ ...DEFAULT_PROFILE })
const form = ref<Profile>({ ...DEFAULT_PROFILE })
const { success, error } = useToaster()

const initials = computed(
  (): string =>
    `${userStore.profile ? userStore.profile.firstName?.charAt(0) : ''}${userStore.profile ? userStore.profile.lastName?.charAt(0) : ''}`,
)

const isDirty = computed((): boolean => !isEqual(form.value, original.value))

const roleName = computed(() => getRoleName(userStore.profile?.role))

const onClose = (): void => {
  validate.value = {}
  open.value = false
  if (isDirty.value) {
    resetForm()
  }
}

const onUpdate = async (): Promise<void> => {
  isSaving.value = true
  const isSuccess = await userStore.updateUserProfile({
    firstName: form.value.firstName,
    lastName: form.value.lastName,
    email: form.value.email,
    phone: form.value.phone,
  })
  isSaving.value = false
  if (isSuccess) {
    original.value = cloneDeep(form.value)
    success('message.savedSuccess')
  } else {
    form.value = cloneDeep(original.value)
    error('error.unexpected')
  }
}

const resetForm = (): void => {
  if (userStore.profile) {
    form.value = cloneDeep(userStore.profile)
    original.value = cloneDeep(form.value)
  }
}

const onLoad = (): void => {
  resetForm()
}

watch(
  open,
  (newVal) => {
    if (newVal) {
      onLoad()
    }
  },
  { immediate: true },
)
</script>

<template>
  <BaseModal :open="open" :confirm="isDirty" @confirm="onClose()">
    <HeaderModal :title="$t('glossary.myProfile')" @close="onClose()" />
    <BodyModal>
      <div class="flex-1">
        <div class="flex justify-center rounded-md pb-6">
          <div
            class="flex items-center justify-center bg-gray-500 rounded-full w-32 h-32 shadow-md"
          >
            <span class="text-5xl text-white uppercase">{{ initials }}</span>
          </div>
        </div>
      </div>
      <div class="flex-1">
        <div class="flex flex-row items-center justify-center gap-4 pb-2">
          <div class="basis-1/2">
            <TextInput
              v-model="form.firstName"
              :label="$t('glossary.firstName')"
              :required="true"
              :disabled="isSaving"
              @validate="(validation) => (validate['firstName'] = validation)"
            />
          </div>
          <div class="basis-1/2">
            <TextInput
              v-model="form.lastName"
              :label="$t('glossary.lastName')"
              :required="true"
              :disabled="isSaving"
              @validate="(validation) => (validate['lastName'] = validation)"
            />
          </div>
        </div>
        <div class="flex flex-row items-center justify-center gap-4 pb-2">
          <div class="basis-1/2">
            <TextInput
              v-model="form.email"
              type="email"
              :label="$t('glossary.email')"
              :required="true"
              :disabled="isSaving"
              @validate="(validation) => (validate['email'] = validation)"
            />
          </div>
          <div class="basis-1/2">
            <TextInput :label="$t('glossary.phone')" v-model="form.phone" :disabled="isSaving" />
          </div>
        </div>
        <div class="flex flex-row items-center justify-center gap-4">
          <div class="basis-1/2">
            <TextInput
              :label="$t('glossary.organization')"
              v-model="form.organization"
              :disabled="true"
            />
          </div>
          <div class="basis-1/2">
            <TextInput :label="$t('glossary.role')" v-model="roleName" :disabled="true" />
          </div>
        </div>
      </div>
    </BodyModal>
    <FooterModal class="justify-end">
      <DefaultButton
        :text="isDirty ? $t('common.cancel') : $t('common.close')"
        @click="onClose()"
      />
      <PrimaryButton
        v-model="validate"
        :text="$t('common.save')"
        :icon="mdiContentSave"
        :disabled="!isDirty"
        :loading="isSaving"
        class="ms-2"
        @click="onUpdate()"
      />
    </FooterModal>
  </BaseModal>
</template>
